import { format } from 'date-fns';
import { supabase } from '../supabase';
import type { CalendarBooking } from '../bookings';

export type CalendarFieldQuoteResource = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url?: string | null;
};

const FIELD_QUOTE_CALENDAR_SELECT = `
  id,
  resource_id,
  project_id,
  business_id,
  customer_id,
  status,
  start_date,
  start_time,
  end_date,
  end_time,
  field_quote_address,
  is_measurement,
  color,
  secondary_color,
  resource:resources!project_field_quotes_resource_id_fkey(
    id,
    first_name,
    last_name,
    avatar_url
  ),
  project_field_quote_resources(
    resource_id,
    resource:resources(id, first_name, last_name, avatar_url)
  ),
  project:projects(title),
  customer:customers(
    first_name,
    last_name,
    email,
    phone
  )
`;

/** Merge primary `resource_id` + junction rows into a de-duplicated resource list. */
export function mergeFieldQuoteResources(row: any): CalendarFieldQuoteResource[] {
  const resources: CalendarFieldQuoteResource[] = [];
  const seen = new Set<string>();
  const push = (r: any) => {
    if (!r?.id || seen.has(r.id)) return;
    seen.add(r.id);
    resources.push({
      id: r.id,
      first_name: r.first_name ?? null,
      last_name: r.last_name ?? null,
      avatar_url: r.avatar_url ?? null,
    });
  };

  const primary = Array.isArray(row.resource) ? row.resource[0] : row.resource;
  if (primary) push(primary);

  for (const link of row.project_field_quote_resources ?? []) {
    const r = Array.isArray(link?.resource) ? link.resource[0] : link?.resource;
    if (r) push(r);
  }

  return resources;
}

function fieldQuoteColor(row: any): string {
  const rawStatus = String(row.status || 'scheduled').toLowerCase();
  const isMeasurement = !!row.is_measurement;
  if (rawStatus === 'completed') return '#22c55e';
  if (rawStatus === 'in progress') return '#eab308';
  if (row.color) return row.color;
  return isMeasurement ? '#8b5cf6' : '#f97316';
}

export function mapFieldQuoteToCalendarEvent(fq: any): CalendarBooking {
  const customerName = fq.customer
    ? `${fq.customer.first_name} ${fq.customer.last_name}`
    : fq.is_measurement
      ? 'Field Measurements'
      : 'Field Quote';

  const startTime = fq.start_time || '09:00';
  const endTime = fq.end_time || startTime;
  const start = fq.start_date
    ? `${fq.start_date}T${startTime.length === 5 ? `${startTime}:00` : startTime}`
    : '';
  const end = fq.end_date
    ? `${fq.end_date}T${endTime.length === 5 ? `${endTime}:00` : endTime}`
    : start;

  const rawStatus = String(fq.status || 'scheduled').toLowerCase();
  const color = fieldQuoteColor(fq);
  const resources = mergeFieldQuoteResources(fq);
  const primaryResource = resources[0] || null;
  const project = Array.isArray(fq.project) ? fq.project[0] : fq.project;

  return {
    id: `fieldquote_${fq.id}`,
    title: fq.is_measurement ? 'Field Measurements' : 'Field Quote',
    booking_number: 0,
    start,
    end,
    backgroundColor: color,
    borderColor: color,
    allDay: false,
    display: 'block',
    resourceId: fq.resource_id || primaryResource?.id,
    extendedProps: {
      customer: customerName,
      customer_phone: fq.customer?.phone || null,
      customer_email: fq.customer?.email || null,
      status: rawStatus,
      secondary_color: fq.secondary_color || null,
      booking_number: 0,
      subtotal: 0,
      total_amount: 0,
      discounts: [],
      payments: [],
      services: [],
      resources,
      primary_resource: primaryResource,
      is_field_quote: true,
      is_measurement: !!fq.is_measurement,
      field_quote_id: fq.id,
      field_quote_status: rawStatus,
      project_id: fq.project_id,
      project_title: project?.title || null,
      booking_address: fq.field_quote_address || null,
      address: fq.field_quote_address || null,
      lat: null,
      lng: null,
      start_time: startTime,
      end_time: endTime,
      resource_id: fq.resource_id || primaryResource?.id || null,
    },
  } as CalendarBooking;
}

/**
 * Field quotes for calendar surfaces.
 * When `filterByResourceId` is set, includes quotes where the resource is primary
 * OR listed in `project_field_quote_resources` (same idea as booking_resources).
 */
export async function getCalendarFieldQuotes(
  businessId: string,
  startDate: Date,
  endDate: Date,
  filterByResourceId?: string
): Promise<CalendarBooking[]> {
  if (!supabase || !businessId) return [];

  const formattedStartDate = format(startDate, 'yyyy-MM-dd');
  const formattedEndDate = format(endDate, 'yyyy-MM-dd');

  let allowedIds: string[] | null = null;

  if (filterByResourceId) {
    const [{ data: junctionRows, error: junctionError }, { data: primaryRows, error: primaryError }] =
      await Promise.all([
        supabase
          .from('project_field_quote_resources')
          .select('project_field_quote_id')
          .eq('resource_id', filterByResourceId),
        supabase
          .from('project_field_quotes')
          .select('id')
          .eq('business_id', businessId)
          .eq('resource_id', filterByResourceId),
      ]);

    if (junctionError) {
      console.error('Error fetching field quote resource links:', junctionError);
      return [];
    }
    if (primaryError) {
      console.error('Error fetching primary field quote assignments:', primaryError);
      return [];
    }

    allowedIds = [
      ...new Set([
        ...(junctionRows || []).map((r) => r.project_field_quote_id).filter(Boolean),
        ...(primaryRows || []).map((r) => r.id).filter(Boolean),
      ]),
    ] as string[];

    if (allowedIds.length === 0) return [];
  }

  let query = supabase
    .from('project_field_quotes')
    .select(FIELD_QUOTE_CALENDAR_SELECT)
    .eq('business_id', businessId)
    .not('start_date', 'is', null)
    .not('end_date', 'is', null)
    .lte('start_date', formattedEndDate)
    .gte('end_date', formattedStartDate)
    .order('start_date', { ascending: true });

  if (allowedIds) {
    query = query.in('id', allowedIds);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error fetching field quotes for calendar:', error);
    return [];
  }

  return (data || []).map(mapFieldQuoteToCalendarEvent);
}
